import {
	faExclamationTriangle,
	faPencilAlt,
	faSpinner,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { useToast } from "@/components/common/Toast";
import SidePanel from "@/components/Sidepanel";
import { useAuthStore } from "@/store/auth.store";
import { useSystemUsersStore } from "@/store/systemUsers.store";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import AddInviteForm from "./AddInviteForm";

export interface User {
	id: string;
	name: string;
	email: string;
	roleId: string;
	status: string;
}

export interface Role {
	id: string;
	name: string;
}

export interface UserWithRole {
	user: User;
	role: Role;
}

const UsersPage: React.FC = () => {
	const queryClient = useQueryClient();
	const { success } = useToast();
	const {
		isInviteModalOpen,
		isEditing,
		formData,
		setFormData,
		openInviteModal,
		closeInviteModal,
		selectedSystemUserId,
		isDeleteModalOpen,
		systemUserToDelete,
		openDeleteModal,
		closeDeleteModal
	} = useSystemUsersStore();

	const { brandId } = useAuthStore();

	const BRAND_USERS_QUERY_KEY = [`brand-users-${brandId}`] as const;

	const {
		data: users = [],
		isLoading,
		isError,
		error
	} = useQuery<UserWithRole[]>({
		queryKey: BRAND_USERS_QUERY_KEY,
		queryFn: async () => {
			const { data } = await apiClient.get(
				resolveEndpoint(API_ENDPOINTS, ["BRAND", "USERS", "LIST"], brandId)
			);
			return data;
		},
		staleTime: 1000 * 60 * 5,
		refetchOnWindowFocus: true,
		refetchOnMount: true
	});

	const createUserMutation = useMutation({
		mutationFn: async (userData: { email: string; roleId: string }) => {
			const { data } = await apiClient.post(
				resolveEndpoint(API_ENDPOINTS, ["BRAND", "USERS", "CREATE"], brandId),
				userData
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BRAND_USERS_QUERY_KEY });
			closeInviteModal();
			success("User created successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to create user");
		}
	});

	const updateUserMutation = useMutation({
		mutationFn: async ({
			id,
			data: { roleId }
		}: {
			id: string;
			data: { roleId: string };
		}) => {
			const { data } = await apiClient.put(
				resolveEndpoint(
					API_ENDPOINTS,
					["BRAND", "USERS", "UPDATE_BY_ID"],
					brandId,
					id
				),
				{ roleId }
			);
			return data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BRAND_USERS_QUERY_KEY });
			closeInviteModal();
			success("User updated successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to update user");
		}
	});

	const deleteUserMutation = useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(
				resolveEndpoint(
					API_ENDPOINTS,
					["BRAND", "USERS", "DELETE_BY_ID"],
					brandId,
					id
				)
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BRAND_USERS_QUERY_KEY });
			success("User deleted successfully");
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to delete user");
		}
	});

	const handleEditClick = (user: UserWithRole) => {
		setFormData({
			email: user.user.email,
			roleId: user.role.id
		});
		openInviteModal(true, user.user.id);
	};

	const handleDeleteClick = (user: UserWithRole) => {
		openDeleteModal(user.user.id, user.user.name);
	};

	const handleConfirmDelete = () => {
		if (systemUserToDelete) {
			deleteUserMutation.mutate(systemUserToDelete.id);
			closeDeleteModal();
		}
	};

	const handleSubmit = (formData: { email: string; roleId: string }) => {
		if (isEditing && selectedSystemUserId) {
			updateUserMutation.mutate({
				id: selectedSystemUserId,
				data: { roleId: formData.roleId }
			});
		} else {
			createUserMutation.mutate(formData);
		}
	};

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<FontAwesomeIcon
					icon={faSpinner}
					spin
					className="h-8 w-8 text-blue-500"
				/>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex items-center justify-center h-screen text-red-600">
				Error: {(error as Error).message}
			</div>
		);
	}

	return (
		<div className="flex-1">
			<div className="flex justify-end items-center mb-6">
				<button
					type="button"
					onClick={() => openInviteModal()}
					className="bg-blue-fynxt text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors text-sm"
				>
					Invite User
				</button>
			</div>

			<div className="bg-white shadow-sm rounded-lg overflow-x-auto overflow-y-auto">
				<table className="min-w-full divide-y divide-gray-200 custom-table">
					<thead className="bg-gray-100 sticky z-10 top-0">
						<tr>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-1/5"
							>
								Name
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-1/5"
							>
								Email
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider  w-1/5"
							>
								Role
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-1/10"
							>
								Status
							</th>
							<th
								scope="col"
								className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider w-1/10"
							>
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-white divide-y divide-gray-200">
						{users.length ? (
							users.map((userWithRole) => (
								<tr key={userWithRole.user.id} className="hover:bg-gray-50">
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{userWithRole.user.name}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{userWithRole.user.email}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
										{userWithRole.role.name}
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center">
										<span
											className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
												userWithRole.user.status === "ACTIVE"
													? "bg-green-100 text-green-600"
													: "bg-gray-100 text-gray-600"
											}`}
										>
											{userWithRole.user.status}
										</span>
									</td>
									<td className="px-6 py-4 whitespace-nowrap text-center">
										<div className="flex justify-center space-x-2">
											<button
												type="button"
												onClick={() => handleEditClick(userWithRole)}
												className="text-green-600 hover:text-green-900 p-1 ml-[5px]"
												title="Edit User"
											>
												<FontAwesomeIcon icon={faPencilAlt} />
											</button>
											<button
												type="button"
												onClick={() => handleDeleteClick(userWithRole)}
												className="text-red-600 hover:text-red-900 p-1"
												title="Delete User"
											>
												<FontAwesomeIcon icon={faTrash} />
											</button>
										</div>
									</td>
								</tr>
							))
						) : (
							<tr>
								<td
									colSpan={5}
									className="px-6 py-6 text-center text-gray-400 text-sm whitespace-nowrap"
								>
									No users data found.
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>

			{isDeleteModalOpen && systemUserToDelete && (
				<div className="fixed inset-0 z-50 overflow-y-auto">
					<div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 transition-opacity"
							aria-hidden="true"
						>
							<div className="absolute inset-0 bg-gray-500 opacity-75" />
						</div>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="sm:flex sm:items-start">
									<div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
										<FontAwesomeIcon
											icon={faExclamationTriangle}
											className="h-6 w-6 text-red-600"
										/>
									</div>
									<div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
										<h3 className="text-lg leading-6 font-medium text-gray-900">
											Delete User
										</h3>
										<div className="mt-2">
											<p className="text-sm text-gray-500">
												Are you sure you want to delete{" "}
												{systemUserToDelete.name}? This action cannot be undone.
											</p>
										</div>
									</div>
								</div>
							</div>
							<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
								<button
									type="button"
									className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={handleConfirmDelete}
								>
									Delete
								</button>
								<button
									type="button"
									className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
									onClick={closeDeleteModal}
								>
									Cancel
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<SidePanel
				isOpen={isInviteModalOpen}
				onClose={closeInviteModal}
				title={isEditing ? "Edit User" : "Invite User"}
				closeOnOutsideClick={false}
			>
				<AddInviteForm
					formData={formData}
					setFormData={setFormData}
					isEditing={isEditing}
					onSubmit={handleSubmit}
				/>
			</SidePanel>
		</div>
	);
};

export default UsersPage;
